import { Tooltip } from '@aecom-ai-engineering/react-components';
import { type Blob, type Finding } from '@consigli/types';
import { type LucideIcon, X } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CONTEXT_KIND_ICONS } from './context-kind-icons';
import { findingContextKind } from './finding-context-kind';
import { useFindingMessage } from './use-finding-message';

const ICON_STROKE_WIDTH = 1.75;

/*
 * Resting on the page's own white with the standard hairline; on hover the
 * brand's pale green behind a #D4D4D4 border, per the design system's
 * context-insight-chip (AI4AECOM Design System, node 11151:21534).
 *
 * Everything but the width, which the two callers set differently: a chip fills
 * the wrapper the strip sizes, the count sizes to its own text. Kept apart
 * because `w-full` and `w-auto` are the same specificity, so composing one over
 * the other left whichever Tailwind emitted last to win - the count took the
 * whole row and squeezed the chip beside it to 20px.
 */
const CHIP_BASE =
  'add-context-chip flex h-[var(--ds-space-8)] min-w-0 items-center gap-[var(--ds-space-2)] rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-[calc(var(--ds-space-2)+var(--ds-space-0-25))] hover:border-[var(--ds-color-raw-neutral-300)] hover:bg-[var(--ds-color-bg-brand-light)]';

const TEXT_CLASS =
  'min-w-0 flex-1 truncate text-left text-[length:var(--ds-typography-paragraph-13-regular-font-size)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] text-[var(--ds-color-raw-stone-600)]';

interface ChipShellProps {
  /** Sizing for the tooltip's wrapper span, which is the strip's flex child. */
  className: string;
  label: string;
  onRemove: () => void;
}

/*
 * The chip is one row: the kind's glyph, the label truncated to a single line,
 * and a remove control. The full label is only ever visible in the tooltip, so
 * the tooltip is the chip's affordance rather than a nicety - hence the whole
 * chip is its trigger, not just the text.
 */
const ChipShell: FC<ChipShellProps & { icon: LucideIcon }> = ({
  className,
  icon: Icon,
  label,
  onRemove,
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip className={`add-context-chip-tooltip ${className}`} content={label} side="top">
      <div className={`${CHIP_BASE} w-full`}>
        <Icon
          aria-hidden="true"
          strokeWidth={ICON_STROKE_WIDTH}
          className="size-[var(--ds-space-4)] shrink-0 text-[var(--ds-color-raw-stone-600)]"
        />
        <span className={TEXT_CLASS}>{label}</span>
        <button
          aria-label={t('prop-chat.add-context-remove', { name: label })}
          className="add-context-chip-remove flex size-[var(--ds-space-4)] shrink-0 cursor-pointer items-center justify-center text-[var(--ds-color-raw-stone-600)]"
          onClick={onRemove}
          type="button"
        >
          <X
            aria-hidden="true"
            strokeWidth={ICON_STROKE_WIDTH}
            className="size-[var(--ds-space-4)]"
          />
        </button>
      </div>
    </Tooltip>
  );
};

/** A finding's chip, labelled with the same headline its card shows. */
export const AddContextFindingChip: FC<{
  className: string;
  finding: Finding;
  onRemove: () => void;
}> = ({ className, finding, onRemove }) => {
  const label = useFindingMessage(finding);

  return (
    <ChipShell
      className={className}
      icon={CONTEXT_KIND_ICONS[findingContextKind(finding)]}
      label={label}
      onRemove={onRemove}
    />
  );
};

/** A document's chip, labelled with its filename. */
export const AddContextDocumentChip: FC<{
  className: string;
  document: Blob;
  onRemove: () => void;
}> = ({ className, document, onRemove }) => (
  <ChipShell
    className={className}
    icon={CONTEXT_KIND_ICONS.document}
    label={document.name}
    onRemove={onRemove}
  />
);

/*
 * The "+4" the collapsed strip ends with. Same shell as a chip, but only a
 * count: no glyph, and nothing to remove.
 *
 * A second way into the same disclosure as the chevron beside it, because the
 * count is what reads as "there are four more" - so it is what a pointer goes
 * for, and a chip-shaped thing that says there is more and does nothing when
 * clicked is the kind of dead end that gets clicked twice.
 *
 * Its accessible name is left as the visible count rather than borrowed from
 * the chevron's label. Two controls announcing the same sentence would be the
 * worse reading of the pair, and `aria-expanded` is what says which state this
 * one acts on - always closed, since the count only exists while collapsed.
 */
export const AddContextMoreChip: FC<{ count: number; onExpand: () => void }> = ({
  count,
  onExpand,
}) => {
  const { t } = useTranslation();

  return (
    <button
      aria-expanded={false}
      className={`${CHIP_BASE} add-context-chip-more w-auto shrink-0 cursor-pointer justify-center`}
      onClick={onExpand}
      type="button"
    >
      <span className={`${TEXT_CLASS} flex-none`}>
        {t('prop-chat.add-context-more-count', { count })}
      </span>
    </button>
  );
};
